import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";

import logo from "../assets/Logo.png";

/* ── Decorative corners ── */
function CornerDecor({ style }) {
  return (
    <svg
      width="70"
      height="70"
      viewBox="0 0 70 70"
      fill="none"
      style={{
        position: "absolute",
        opacity: 0.16,
        pointerEvents: "none",
        ...style,
      }}
    >
      <path
        d="M0 70 L0 0 L70 0"
        stroke="rgba(201,151,58,0.22)"
        strokeWidth="1"
      />

      <path
        d="M12 70 L12 12 L70 12"
        stroke="rgba(201,151,58,0.08)"
        strokeWidth="1"
      />
    </svg>
  );
}

/* ── Animation variants ── */
const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.14,
      delayChildren: 0.2,
    },
  },
};

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 35,
  },

  show: {
    opacity: 1,
    y: 0,

    transition: {
      duration: 1,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const softScale = {
  hidden: {
    opacity: 0,
    scale: 0.85,
  },

  show: {
    opacity: 1,
    scale: 1,

    transition: {
      duration: 1.2,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

export default function Hero() {
  const ref = useRef(null);

  /* ── Scroll effects ── */
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const contentY = useTransform(
    scrollYProgress,
    [0, 1],
    [0, 120]
  );

  const contentOpacity = useTransform(
    scrollYProgress,
    [0, 0.55],
    [1, 0]
  );

  return (
    <section
      ref={ref}
      style={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "radial-gradient(circle at top, #241b12 0%, #14110c 42%, #090807 100%)",
      }}
    >
      {/* ── Internal styles ── */}
      <style>{`
        @keyframes gridMove {
          from {
            transform: translate3d(0,0,0);
          }

          to {
            transform: translate3d(70px,70px,0);
          }
        }

        @keyframes floating {
          0% {
            transform: translateY(0px);
          }

          50% {
            transform: translateY(-12px);
          }

          100% {
            transform: translateY(0px);
          }
        }

        @media (max-width: 1024px) {
          .hero-content {
            padding-top: 10rem !important;
            padding-bottom: 5rem !important;
          }

          .hero-title-main {
            font-size: clamp(3.4rem, 11vw, 5rem) !important;
          }

          .hero-title-gold {
            font-size: clamp(3rem, 10vw, 4.8rem) !important;
          }

          .hero-subtitle {
            font-size: 1.05rem !important;
            line-height: 1.7 !important;
            padding: 0 1rem !important;
          }

          .hero-buttons {
            gap: 1rem !important;
          }

          .hero-button {
            width: 100%;
            max-width: 340px;
            text-align: center;
          }
        }

        @media (max-width: 640px) {
          .hero-content {
            padding-top: 8.5rem !important;
            padding-left: 1.25rem !important;
            padding-right: 1.25rem !important;
          }

          .hero-location {
            font-size: 0.62rem !important;
            letter-spacing: 0.38em !important;
            margin-bottom: 1.5rem !important;
          }

          .hero-logo {
            width: 92px !important;
            margin-bottom: 1.8rem !important;
          }

          .hero-title-main {
            font-size: clamp(3rem, 16vw, 4.2rem) !important;
            line-height: 0.95 !important;
          }

          .hero-title-gold {
            font-size: clamp(2.8rem, 14vw, 4rem) !important;
            line-height: 0.95 !important;
            margin-bottom: 1rem !important;
          }

          .hero-subtitle {
            font-size: 0.95rem !important;
            letter-spacing: 0.08em !important;
            margin-bottom: 2.4rem !important;
          }

          .hero-buttons {
            flex-direction: column !important;
            width: 100%;
            align-items: center !important;
          }

          .hero-button {
            width: 100%;
            max-width: 100%;
            font-size: 0.72rem !important;
            padding: 1rem 1.2rem !important;
            letter-spacing: 0.22em !important;
          }

          .corner-decor {
            display: none !important;
          }
        }
      `}</style>

      {/* ── Animated grid ── */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(201,151,58,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(201,151,58,0.035) 1px, transparent 1px)",
          backgroundSize: "70px 70px",
          animation: "gridMove 22s linear infinite",
        }}
      />

      {/* ── Main glow ── */}
      <div
        style={{
          position: "absolute",
          width: "900px",
          height: "900px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(201,151,58,0.12) 0%, rgba(201,151,58,0.04) 35%, transparent 72%)",
          left: "50%",
          top: "42%",
          transform: "translate(-50%, -50%)",
          filter: "blur(10px)",
          pointerEvents: "none",
        }}
      />

      {/* ── Secondary blur glow ── */}
      <div
        style={{
          position: "absolute",
          width: "420px",
          height: "420px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,220,140,0.08) 0%, transparent 70%)",
          filter: "blur(40px)",
          top: "18%",
          left: "50%",
          transform: "translateX(-50%)",
          pointerEvents: "none",
        }}
      />

      {/* ── Decorative corners ── */}
      <div className="corner-decor">
        <CornerDecor
          style={{
            top: "2rem",
            left: "2rem",
          }}
        />

        <CornerDecor
          style={{
            top: "2rem",
            right: "2rem",
            transform: "rotate(90deg)",
          }}
        />

        <CornerDecor
          style={{
            bottom: "2rem",
            left: "2rem",
            transform: "rotate(-90deg)",
          }}
        />

        <CornerDecor
          style={{
            bottom: "2rem",
            right: "2rem",
            transform: "rotate(180deg)",
          }}
        />
      </div>

      {/* ── Content ── */}
      <motion.div
        className="hero-content"
        style={{
          y: contentY,
          opacity: contentOpacity,
          position: "relative",
          zIndex: 2,
          width: "100%",
          maxWidth: "1400px",
          textAlign: "center",
          padding:
            "4rem 2rem 6rem",
        }}
      >
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
        >
          {/* ── Location ── */}
          <motion.p
            variants={fadeUp}
            className="hero-location"
            style={{
              fontFamily: "'Jost', sans-serif",
              fontSize: "0.72rem",
              fontWeight: 400,
              letterSpacing: "0.52em",
              textTransform: "uppercase",
              color: "#c9973a",
              marginBottom: "2rem",
            }}
          >
            Santa Clara del Mar
          </motion.p>

          {/* ── Logo ── */}
          <motion.div
            variants={softScale}
            className="hero-logo"
            style={{
              width: "clamp(110px, 12vw, 150px)",
              margin: "0 auto 2.6rem",
              animation:
                "floating 5s ease-in-out infinite",
            }}
          >
            <img
              src={logo}
              alt="Monaco Aberturas"
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                filter:
                  "drop-shadow(0 0 35px rgba(201,151,58,0.18))",
              }}
            />
          </motion.div>

          {/* ── Main title ── */}
          <motion.h1
            variants={fadeUp}
            className="hero-title-main"
            style={{
              fontFamily:
                "'Cormorant Garamond', serif",
              fontSize:
                "clamp(4.5rem, 8vw, 8rem)",
              fontWeight: 300,
              lineHeight: 0.9,
              letterSpacing: "-0.04em",
              color: "#f8f3eb",
              marginBottom: "0.2rem",
              textShadow:
                "0 10px 40px rgba(0,0,0,0.4)",
            }}
          >
            Monaco
          </motion.h1>

          {/* ── Gold title ── */}
          <motion.div variants={fadeUp}>
            <span
              className="hero-title-gold"
              style={{
                display: "block",
                fontFamily:
                  "'Cormorant Garamond', serif",
                fontSize:
                  "clamp(4rem, 7vw, 7rem)",
                fontWeight: 700,
                lineHeight: 0.95,
                letterSpacing: "0.02em",
                marginBottom: "1.3rem",
                background:
                  "linear-gradient(135deg, #fff0bc 0%, #f0c96b 24%, #c9973a 50%, #ffe3a0 75%, #c9973a 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                filter:
                  "drop-shadow(0 0 24px rgba(201,151,58,0.14))",
              }}
            >
              Aberturas
            </span>
          </motion.div>

          {/* ── Subtitle ── */}
          <motion.p
            variants={fadeUp}
            className="hero-subtitle"
            style={{
              maxWidth: "760px",
              margin: "0 auto 3.5rem",
              fontFamily:
                "'Cormorant Garamond', serif",
              fontSize:
                "clamp(1.05rem, 2vw, 1.55rem)",
              fontWeight: 300,
              fontStyle: "italic",
              color: "#e9e1d5",
              letterSpacing: "0.1em",
              lineHeight: 1.5,
            }}
          >
            Soluciones de Vanguardia en Aluminio
          </motion.p>

          {/* ── Buttons ── */}
          <motion.div
            variants={fadeUp}
            className="hero-buttons"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "1.2rem",
            }}
          >
            <Link
              to="/productos"
              style={{
                textDecoration: "none",
              }}
            >
              <motion.div
                className="hero-button"
                whileHover={{
                  y: -3,
                  scale: 1.02,
                  boxShadow:
                    "0 18px 60px rgba(201,151,58,0.28)",
                }}
                whileTap={{
                  scale: 0.98,
                }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                }}
                style={{
                  padding:
                    "1rem 2.6rem",
                  background:
                    "linear-gradient(135deg, #7f5617 0%, #c9973a 55%, #f7d789 100%)",
                  color: "#17140f",
                  fontFamily:
                    "'Jost', sans-serif",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  letterSpacing: "0.25em",
                  textTransform: "uppercase",
                }}
              >
                Conocé nuestros productos
              </motion.div>
            </Link>

            <motion.a
              href="https://wa.me/542236901020"
              target="_blank"
              rel="noreferrer"
              className="hero-button"
              whileHover={{
                y: -3,
                background:
                  "rgba(201,151,58,0.08)",
                borderColor: "#f0c96b",
              }}
              whileTap={{
                scale: 0.98,
              }}
              transition={{
                type: "spring",
                stiffness: 260,
              }}
              style={{
                padding:
                  "1rem 2.6rem",
                border:
                  "1px solid rgba(201,151,58,0.45)",
                color: "#f0c96b",
                textDecoration: "none",
                fontFamily:
                  "'Jost', sans-serif",
                fontSize: "0.78rem",
                fontWeight: 400,
                letterSpacing: "0.25em",
                textTransform: "uppercase",
                backdropFilter: "blur(10px)",
                transition:
                  "border-color 0.3s ease, background 0.3s ease",
              }}
            >
              WhatsApp directo
            </motion.a>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
}